import React from 'react';
import { BsFillExclamationOctagonFill } from 'react-icons/bs';
import { GeneralIndicator, Icon } from 'core/components';

export const DOWNSAMPLED_THRESHOLD = 1;
export const LOW_RESOLUTION_THRESHOLD = 5;

/**
 * Icon component indicating downsampled flow data
 * @returns {JSX.Element} Warning triangle icon in orange
 */
const DownsampledIcon = () => <Icon icon="warning-sign" color="warning" />;
/**
 * Icon component indicating low resolution flow data
 * @returns {JSX.Element} Warning octagon icon in red
 */
const LowResIcon = () => <Icon icon={BsFillExclamationOctagonFill} color="danger" />;

/**
 * Full indicator tag component for downsampled data
 * Displays warning icon and 'Downsampled' label
 * @param {Object} props - Component props
 * @param {boolean} [props.interactive] - Whether the tag should be interactive
 * @param {import('react').CSSProperties['fontWeight']} [props.fontWeight] - Font weight for the indicator text
 * @param {string} [props.text='Downsampled'] - Custom text to display in the indicator
 * @returns {JSX.Element} Tag component with downsampled indicator
 */
export function DownsampledIndicator({ interactive, fontWeight, text = 'Downsampled' }) {
  return <GeneralIndicator intent="warning" text={text} interactive={interactive} fontWeight={fontWeight} />;
}

/**
 * Full indicator tag component for low resolution data
 * Displays danger icon and 'Low Resolution' label
 * @param {Object} props - Component props
 * @param {boolean} [props.interactive] - Whether the tag should be interactive
 * @param {import('react').CSSProperties['fontWeight']} [props.fontWeight] - Font weight for the indicator text
 * @param {string} [props.text='Low Resolution'] - Custom text to display in the indicator
 * @returns {JSX.Element} Tag component with low resolution indicator
 */
export function LowResolutionIndicator({ interactive, fontWeight, text = 'Low Resolution' }) {
  return <GeneralIndicator intent="danger" text={text} interactive={interactive} fontWeight={fontWeight} />;
}

/**
 * Full indicator tag component for good resolution data
 * Displays success icon and 'Full Resolution' label
 * @param {Object} props - Component props
 * @param {boolean} [props.interactive] - Whether the tag should be interactive
 * @param {import('react').CSSProperties['fontWeight']} [props.fontWeight] - Font weight for the indicator text
 * @param {string} [props.text='Full Resolution'] - Custom text to display in the indicator
 * @returns {JSX.Element} Tag component with full resolution indicator
 */
export function GoodResolutionIndicator({ interactive, fontWeight, text = 'Full Resolution' }) {
  return <GeneralIndicator intent="success" text={text} interactive={interactive} fontWeight={fontWeight} />;
}
/**
 * Component to display full data resolution indicator tag
 *
 * Shows visual warning when flow data has been downsampled or has low resolution.
 * Displays nothing when data has full resolution.
 *
 * @param {Object} props - Component props
 * @param {number} [props.flowSampleRate] - The flow sample rate value (higher = lower resolution)
 * @param {boolean} [props.showGoodResolutionIndicator=false] - Whether to show indicator when resolution is good
 * @param {boolean} [props.interactive] - Whether the tag should be interactive
 * @returns {JSX.Element} Tag indicator or empty fragment
 */
export function DataResolutionIndicator({ flowSampleRate, showGoodResolutionIndicator = false, interactive = false }) {
  let indicator = <></>;

  if (flowSampleRate && flowSampleRate > LOW_RESOLUTION_THRESHOLD) {
    indicator = <LowResolutionIndicator interactive={interactive} />;
  } else if (flowSampleRate && flowSampleRate > DOWNSAMPLED_THRESHOLD) {
    indicator = <DownsampledIndicator interactive={interactive} />;
  } else if (flowSampleRate && showGoodResolutionIndicator && flowSampleRate <= DOWNSAMPLED_THRESHOLD) {
    indicator = <GoodResolutionIndicator interactive={interactive} />;
  }

  return indicator;
}

/**
 * Component to display data resolution indicator icon only (no label)
 *
 * Shows a compact icon warning when flow data has been downsampled or has low resolution.
 * Useful for space-constrained layouts. Displays nothing when data has full resolution.
 *
 * @param {Object} props - Component props
 * @param {number} [props.flowSampleRate=DOWNSAMPLED_THRESHOLD] - The flow sample rate value
 * @returns {JSX.Element} Icon component or empty fragment
 */
export function DataResolutionIcon({ flowSampleRate = DOWNSAMPLED_THRESHOLD }) {
  let icon = <></>;

  if (flowSampleRate > LOW_RESOLUTION_THRESHOLD) {
    icon = <LowResIcon />;
  } else if (flowSampleRate > DOWNSAMPLED_THRESHOLD) {
    icon = <DownsampledIcon />;
  }

  return icon;
}
